import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  getIdToken,
  PhoneAuthProvider,
  signInWithCredential,
  signInWithPhoneNumber,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth } from "../../firebase";
import config from "../../config.js";
import { useNavigate } from "react-router-dom";
import HomeHeader from "../../components/header/homeHeader/HomeHeader";
import { useUser } from "./UserContextPersistent";
import "./Login.css";
import UniversalCookie from "universal-cookie";
import LoginLeftContainer from "./LoginLeftContainer";

const API_URL = `${config.baseURL}/auth/login?type=firebase`;

const LoginOtp = () => {
  const navigate = useNavigate();
  const { dispatch } = useUser();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timerCount, setTimerCount] = useState(45);
  const [timerActive, setTimerActive] = useState(true);
  const [canResend, setCanResend] = useState(false);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const recaptchaVerifierRef = useRef(null);
  const navigationTimeoutRef = useRef(null);
  const MAX_VERIFICATION_ATTEMPTS = 3;
  const [focusedIndex, setFocusedIndex] = useState(0);
  // Notification state
  const [notification, setNotification] = useState({
    message: "",
    type: "success",
    open: false,
  });

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleInputFocus = (index) => {
    setFocusedIndex(index);
  };
  const handleInputBlur = () => {
    // Keep the focused index for visual feedback
  };

  const getInputStyle = (index) => {
    const isFocused = focusedIndex === index;
    const hasValue = otp[index] !== "";
    const isDisabled = loading || isVerifying || isResending;

    return {
      width: "40px",
      height: "48px",
      textAlign: "center",
      fontSize: "20px",
      fontWeight: "500",
      border: `2px solid ${isDisabled
          ? "#E0E0E0"
          : isFocused
            ? "#20A8E0"
            : hasValue
              ? "#333333"
              : "#CCCCCC"
        }`,
      borderRadius: "8px",
      outline: "none",
      backgroundColor: isDisabled ? "#F5F5F5" : "#FFFFFF",
      color: isDisabled ? "#999999" : "#000000",
      cursor: isDisabled ? "not-allowed" : "text",
      transition: "all 0.2s ease-in-out",
      boxShadow: isFocused ? "0 0 0 3px rgba(32, 168, 224, 0.1)" : "none",
      caretColor: isFocused && !hasValue ? "#20A8E0" : "transparent",
    };
  };
  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    // Retrieve saved phone number and verification ID from sessionStorage
    const storedPhoneNumber = sessionStorage.getItem("phoneNumber");
    const storedVerificationId = sessionStorage.getItem("verificationId");

    if (storedPhoneNumber) {
      setPhoneNumber(storedPhoneNumber);
    }

    if (storedVerificationId) {
      setVerificationId(storedVerificationId);
    } else {
      // If no verification ID is found, redirect back to phone input
      navigate("/auth-phone");
    }

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (navigationTimeoutRef.current)
        clearTimeout(navigationTimeoutRef.current);
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          //console.warn("Error clearing reCAPTCHA:", e);
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, [navigate]);

  // Timer for OTP resend
  useEffect(() => {
    if (timerActive && timerCount > 0) {
      timerRef.current = setTimeout(() => {
        if (mountedRef.current) setTimerCount((prev) => prev - 1);
      }, 1000);
    } else if (timerCount === 0) {
      if (mountedRef.current) {
        setCanResend(true);
        setTimerActive(false);
      }
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timerActive, timerCount]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) inputRefs.current[0].focus();
    setFocusedIndex(0);
  }, []);

  // Show notification helper
  const showNotification = (message, type = "success") => {
    if (!mountedRef.current) return;

    setNotification({
      message,
      type,
      open: true,
    });
  };

  // Handle notification close
  const handleNotificationClose = (event, reason) => {
    if (reason === "clickaway") return;
    setNotification({ ...notification, open: false });
  };

  // Reset authentication state
  const resetAuthState = () => {
    // Clear any stored tokens from previous attempts
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("verificationId");
    sessionStorage.removeItem("phoneNumber");

    const cookies = new UniversalCookie();
    cookies.remove("token", { path: "/" });

    // Reset auth-related state
    setVerificationId(null);

    // Reset user state in context
    dispatch({ type: "CLEAR_USER" });
  };

  // Handle successful login with safe navigation delay
  const handleLoginSuccess = (message = "Login successful!") => {
    showNotification(message);

    // Only navigate to dashboard when explicitly authorized
    if (
      message === "Phone verification successful!" ||
      message === "Login successful!"
    ) {
      // Clear any existing timeout to prevent multiple navigations
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      // Set new timeout for navigation
      navigationTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          let savedRedirectUrl = null;
          try {
            const searchParams = new URLSearchParams(window.location.search);
            const queryRedirect = searchParams.get("redirect");
            if (queryRedirect && queryRedirect !== "/" && queryRedirect !== "/login") {
              savedRedirectUrl = decodeURIComponent(queryRedirect);
            }
          } catch (e) { }
          if (!savedRedirectUrl) {
            savedRedirectUrl = sessionStorage.getItem("redirectUrl") || localStorage.getItem("redirectUrl");
          }

          if (savedRedirectUrl && savedRedirectUrl !== "/" && savedRedirectUrl !== "/login") {
            sessionStorage.removeItem("redirectUrl");
            localStorage.removeItem("redirectUrl");
            localStorage.setItem("selectedApp", "vehicleTrack");
            navigate(savedRedirectUrl);
          } else {
            navigate("/dashboard");
          }
        }
      }, 1500);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (e.nativeEvent.inputType === "insertFromPaste") {
        const pastedText = e.clipboardData?.getData("text") || "";
        const digits = pastedText.replace(/\D/g, "").split("").slice(0, 6);
        if (digits.length > 0) {
          const newFullOtp = [...otp];
          digits.forEach((digit, idx) => {
            if (index + idx < 6) newFullOtp[index + idx] = digit;
          });
          setOtp(newFullOtp);
          const nextEmptyIndex = newFullOtp.findIndex((d) => d === "");
          if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
            inputRefs.current[nextEmptyIndex].focus();
          } else if (digits.length + index < 6) {
            inputRefs.current[digits.length + index].focus();
          } else {
            inputRefs.current[5].focus();
          }
          return;
        }
      }

      if (value !== "" && index < 5) {
        inputRefs.current[index + 1].focus();
        setFocusedIndex(index + 1);
      }
    }
  };

  // Handle keyboard navigation between OTP inputs
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
        setFocusedIndex(index - 1);
      } else if (otp[index] !== "") {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
      setFocusedIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1].focus();
      setFocusedIndex(index + 1);
    }
  };

  // Handle paste event for OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length) {
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (idx < 6) newOtp[idx] = digit;
      });
      setOtp(newOtp);
      const nextEmptyIndex = newOtp.findIndex((d) => d === "");
      if (nextEmptyIndex !== -1 && nextEmptyIndex < 6) {
        inputRefs.current[nextEmptyIndex].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  // Handle OTP verification
  const handleVerify = async () => {
    if (!isOtpComplete || !verificationId || isVerifying) return;

    if (verificationAttempts >= MAX_VERIFICATION_ATTEMPTS) {
      showNotification(
        "Too many failed attempts. Please request a new OTP.",
        "error"
      );
      return;
    }

    setIsVerifying(true);
    setLoading(true);
    const enteredOtp = otp.join("");

    try {
      //console.log("Starting OTP verification");

      // Create a credential using the verification ID and OTP
      const phoneAuthCredential = PhoneAuthProvider.credential(
        verificationId,
        enteredOtp
      );

      // Sign in with the credential
      const userCredential = await signInWithCredential(
        auth,
        phoneAuthCredential
      );
      const user = userCredential.user;

      //console.log("Retrieving ID token");
      const token = await getIdToken(user);
      //console.log("ID token retrieved");

      sessionStorage.setItem("authToken", token);

      //console.log("Sending backend request");
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firebaseToken: token }),
        credentials: "include",
      });
      //console.log("Backend response received");

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Backend verification failed");
      }

      const data = await response.json();
      dispatch({
        type: "SET_USER",
        payload: {
          ...data.tokenUser.tokenuser,
          role: data.tokenUser.userRoleWithAccess.name,
        },
      });
      dispatch({
        type: "SET_FEATURES",
        payload: data.tokenUser.userRoleWithAccess.features.map((feature) => ({
          action: feature.feature.action,
          api_path: feature.feature.api_path,
          name: feature.feature.name,
          description: feature.feature.description,
          isDefault: feature.feature.isDefault,
          displayName: feature.feature.displayName,
        })),
      });

      const cookies = new UniversalCookie();
      cookies.set("token", token, {
        path: "/",
        secure: true,
        sameSite: "strict",
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      });

      setOtp(["", "", "", "", "", ""]);

      // Clean up sessionStorage items that are no longer needed
      sessionStorage.removeItem("verificationId");
      sessionStorage.removeItem("phoneNumber");

      if (mountedRef.current) {
        handleLoginSuccess("Phone verification successful!");
      }
    } catch (error) {
      //console.error("OTP verification error:", error);
      setVerificationAttempts((prev) => prev + 1);

      let errorMessage = "Invalid OTP. Please try again.";
      if (error.code === "auth/code-expired") {
        errorMessage = "OTP has expired. Please request a new code.";
        setCanResend(true);
        setTimerActive(false);
        setTimerCount(0);
      } else if (error.code === "auth/invalid-verification-code") {
        errorMessage = `Invalid OTP. ${MAX_VERIFICATION_ATTEMPTS - verificationAttempts - 1
          } attempts remaining.`;
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (error.code === "auth/network-request-failed") {
        errorMessage =
          "Network error. Please check your connection and try again.";
      } else if (error.message.includes("Backend verification failed")) {
        errorMessage = "Authentication failed. Please try again later.";
      }

      sessionStorage.removeItem("authToken");
      showNotification(errorMessage, "error");

      setOtp(["", "", "", "", "", ""]);
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    } finally {
      if (mountedRef.current) {
        setIsVerifying(false);
        setLoading(false);
      }
    }
  };

  // Fixed handleResendOtp function - simplified approach using signInWithPhoneNumber
  const handleResendOtp = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    setLoading(true);

    try {
      const formattedPhone = phoneNumber.trim().replace(/[\s()-]/g, "");
      //console.log("Preparing to resend OTP to:", formattedPhone);

      // Clear any existing reCAPTCHA instance
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          //console.warn("Error clearing existing reCAPTCHA:", e);
        }
        recaptchaVerifierRef.current = null;
      }

      // Initialize invisible reCAPTCHA (same as in LoginPhone.jsx)
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            //console.log("reCAPTCHA verified successfully for resend");
          },
          "expired-callback": () => {
            //console.warn("reCAPTCHA expired for resend");
            if (mountedRef.current) {
              showNotification(
                "Security verification expired. Please try again.",
                "error"
              );
            }
          },
        }
      );

      await recaptchaVerifierRef.current.render();
      //console.log("reCAPTCHA rendered for resend");

      // Use signInWithPhoneNumber (same method as initial OTP send)
      const confirmationResultObj = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );

      if (!mountedRef.current) return;

      // Update the verification ID in component state and session storage
      const newVerificationId = confirmationResultObj.verificationId;
      setVerificationId(newVerificationId);
      sessionStorage.setItem("verificationId", newVerificationId);

      // Reset form state
      setOtp(["", "", "", "", "", ""]);
      setVerificationAttempts(0);
      setTimerCount(45);
      setTimerActive(true);
      setCanResend(false);

      if (inputRefs.current[0]) inputRefs.current[0].focus();

      showNotification(`New verification code sent to ${formattedPhone}`);
    } catch (error) {
      //console.error("Error in OTP resend process:", error);
      let errorMsg = "Failed to send verification code. Please try again.";

      if (error.code === "auth/invalid-phone-number") {
        errorMsg =
          "The phone number is invalid. Please check the format (e.g., +12025550123).";
      } else if (error.code === "auth/captcha-check-failed") {
        errorMsg =
          "Security verification failed. Please try again or disable ad blockers.";
      } else if (error.code === "auth/quota-exceeded") {
        errorMsg = "SMS quota exceeded. Please try again later.";
      } else if (error.code === "auth/too-many-requests") {
        errorMsg = "Too many attempts. Please try again later.";
      } else if (error.code === "auth/network-request-failed") {
        errorMsg = "Network error. Please check your connection and try again.";
      }

      showNotification(errorMsg, "error");
    } finally {
      // Clean up reCAPTCHA
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          //console.warn("Error clearing reCAPTCHA:", e);
        }
        recaptchaVerifierRef.current = null;
      }
      if (mountedRef.current) {
        setIsResending(false);
        setLoading(false);
      }
    }
  };

  // Handle back to login with state reset
  const handleBackToLoginWithReset = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    resetAuthState();
    navigate("/");
  };

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <>
      <HomeHeader />
      <div className="login_container">
        <div className="content_container">
          {/* Left side content */}
          <LoginLeftContainer />

          {/* Right side OTP form */}
          <div
            className="container_container2"
            style={{
              border: "1px solid #A5DCF3",
              boxShadow: "-2px 2px 5px 0px #00000040",
            }}
          >
            <div style={{ padding: "1rem", width: "100%" }}>
              <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                <h2
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "500",
                    marginBottom: "0.25rem",
                  }}
                >
                  Enter the OTP
                </h2>
                <p
                  style={{
                    fontSize: "0.875rem",
                    color: "#666",
                    marginTop: "0.25rem",
                  }}
                >
                  We've sent a 6-digit code to {phoneNumber}
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "1.5rem",
                  gap: "0.5rem",
                }}
                onPaste={handlePaste}
              >
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    maxLength="1"
                    value={otp[index]}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={() => handleInputFocus(index)}
                    onBlur={handleInputBlur}
                    style={getInputStyle(index)}
                    disabled={loading || isVerifying || isResending}
                    autoComplete="one-time-code"
                    placeholder={
                      focusedIndex === index && otp[index] === "" ? "|" : ""
                    }
                  />
                ))}
              </div>
              <Button
                onClick={handleVerify}
                disabled={
                  !isOtpComplete ||
                  loading ||
                  isVerifying ||
                  isResending ||
                  verificationAttempts >= MAX_VERIFICATION_ATTEMPTS
                }
                variant="contained"
                style={{
                  width: "100%",
                  borderRadius: "16px",
                  marginTop: "20px",
                  backgroundColor:
                    isOtpComplete &&
                      verificationAttempts < MAX_VERIFICATION_ATTEMPTS
                      ? "#FFFF00"
                      : "#E0E0E0",
                  color:
                    isOtpComplete &&
                      verificationAttempts < MAX_VERIFICATION_ATTEMPTS
                      ? "#000000"
                      : "#999999",
                  boxShadow: "none",
                  fontWeight: "600",
                  fontSize: "18px",
                  textTransform: "none",
                  height: "48px",
                  position: "relative",
                }}
              >
                {loading || isVerifying ? (
                  <CircularProgress
                    size={24}
                    sx={{ color: "#000000", position: "absolute" }}
                  />
                ) : (
                  "Verify"
                )}
              </Button>
              <div style={{ textAlign: "center", marginTop: "1rem" }}>
                {!canResend ? (
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Resend OTP in {formatTime(timerCount)}
                  </span>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "5px",
                    }}
                  >
                    <span style={{ fontSize: "14px", color: "#666" }}>
                      Didn't receive the code?
                    </span>
                    <Button
                      onClick={handleResendOtp}
                      disabled={loading || isVerifying || isResending}
                      style={{
                        color: "#20A8E0",
                        fontSize: "14px",
                        fontWeight: "500",
                        textTransform: "none",
                        padding: "4px 8px",
                        minWidth: "auto",
                        opacity:
                          loading || isVerifying || isResending ? 0.5 : 1,
                      }}
                    >
                      {isResending ? "Sending..." : "Resend OTP"}
                    </Button>
                  </div>
                )}
              </div>
              <div id="recaptcha-container" />
              <Box sx={{ marginTop: "20px", textAlign: "center" }}>
                <Button
                  onClick={handleBackToLoginWithReset}
                  disabled={loading || isVerifying || isResending}
                  style={{
                    color: "#20A8E0",
                    textTransform: "none",
                    fontSize: "16px",
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced notification system */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ marginTop: "70px" }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LoginOtp;
